// =====================================================
// MEDICAL ACTS API CLIENT
// ACTUALIZADO: Relaciones N:M con especialidades, subespecialidades y grupos
// =====================================================

import { api } from './client';
import type { PaginationQuery, PaginatedResponse } from '../types/pagination';
import type { OdontogramType } from '../types/catalog-admin';

// ----------------------------------------------------------------------------
// TIPOS ACTUALIZADOS
// ----------------------------------------------------------------------------

export interface SpecialtyInfo {
  id: string;
  code: string;
  name: string;
}

export interface SubspecialtyInfo {
  id: string;
  code: string;
  name: string;
  specialtyId: string;
  specialtyName: string;
}

export interface TreatmentGroupInfo {
  id: string;
  code: string;
  name: string;
}

export interface MedicalAct {
  id: string;
  companyId: string;
  code: string;
  name: string;
  description: string | null;
  requiresOdontogram: boolean;
  odontogramType: OdontogramType | null;
  defaultPrice: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  linkedSitesCount?: number;
  // NUEVO: Arrays de relaciones
  specialties: SpecialtyInfo[];
  subspecialties: SubspecialtyInfo[];
  treatmentGroups: TreatmentGroupInfo[];
  // Indicadores de precio
  hasSitePrice?: boolean;
  hasTariffPrice?: boolean;
  isUnpriced?: boolean;
}

export interface MedicalActSelect {
  id: string;
  code: string;
  name: string;
  requiresOdontogram: boolean;
  odontogramType: OdontogramType | null;
  defaultPrice: number;
  specialtyIds: string[];
  subspecialtyIds: string[];
  treatmentGroupIds: string[];
}

export interface CreateMedicalActRequest {
  code: string;
  name: string;
  description?: string;
  requiresOdontogram?: boolean;
  odontogramType?: OdontogramType | null;
  defaultPrice?: number;
  sortOrder?: number;
  specialtyIds?: string[];
  subspecialtyIds?: string[];
  treatmentGroupIds?: string[];
}

export interface UpdateMedicalActRequest {
  code?: string;
  name?: string;
  description?: string;
  requiresOdontogram?: boolean;
  odontogramType?: OdontogramType | null;
  defaultPrice?: number;
  isActive?: boolean;
  sortOrder?: number;
  specialtyIds?: string[];
  subspecialtyIds?: string[];
  treatmentGroupIds?: string[];
}

export interface MedicalActLinkedSite {
  siteId: string;
  siteCode: string;
  siteName: string;
  isLinked: boolean;
  isActive: boolean;
  priceOverride: number | null;
  societies: { id: string; code: string; name: string }[];
  availableSocieties: { id: string; code: string; name: string; isDefault: boolean }[];
}

export interface UpdateMedicalActSiteLinksRequest {
  links: {
    siteId: string;
    isLinked: boolean;
    priceOverride?: number | null;
    societyIds?: string[];
  }[];
}

export interface MedicalActsPaginationQuery extends PaginationQuery {
  specialtyId?: string;
  subspecialtyId?: string;
  treatmentGroupId?: string;
  unpricedOnly?: boolean;
}

export interface PaginatedMedicalActsResponse extends PaginatedResponse<MedicalAct> {
  meta: PaginatedResponse<MedicalAct>['meta'] & {
    unpricedCount?: number;
  };
}

// ----------------------------------------------------------------------------
// API CLIENT
// ----------------------------------------------------------------------------

const BASE_URL = '/medical-acts';

export const medicalActsApi = {
  /**
   * Listar con paginación - ACTUALIZADO: nuevos filtros
   */
  getAllPaginated: async (
    params: MedicalActsPaginationQuery = {},
  ): Promise<PaginatedMedicalActsResponse> => {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.search) searchParams.set('search', params.search);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.includeInactive) searchParams.set('includeInactive', 'true');
    if (params.specialtyId) searchParams.set('specialtyId', params.specialtyId);
    if (params.subspecialtyId) searchParams.set('subspecialtyId', params.subspecialtyId);
    if (params.treatmentGroupId) searchParams.set('treatmentGroupId', params.treatmentGroupId);
    if (params.unpricedOnly) searchParams.set('unpricedOnly', 'true');

    const queryString = searchParams.toString();
    const url = `${BASE_URL}${queryString ? `?${queryString}` : ''}`;

    return api.get<PaginatedMedicalActsResponse>(url);
  },

  /**
   * Obtener lista para select - ACTUALIZADO: nuevos filtros
   */
  getForSelect: async (filters?: {
    specialtyId?: string;
    subspecialtyId?: string;
    treatmentGroupId?: string;
  }): Promise<MedicalActSelect[]> => {
    const params = new URLSearchParams();
    if (filters?.specialtyId) params.set('specialtyId', filters.specialtyId);
    if (filters?.subspecialtyId) params.set('subspecialtyId', filters.subspecialtyId);
    if (filters?.treatmentGroupId) params.set('treatmentGroupId', filters.treatmentGroupId);
    const query = params.toString();
    return api.get<MedicalActSelect[]>(`${BASE_URL}/select${query ? `?${query}` : ''}`);
  },

  /**
   * Obtener por ID
   */
  getById: async (id: string): Promise<MedicalAct> => {
    return api.get<MedicalAct>(`${BASE_URL}/${id}`);
  },

  /**
   * Obtener centros vinculados
   */
  getLinkedSites: async (id: string): Promise<MedicalActLinkedSite[]> => {
    return api.get<MedicalActLinkedSite[]>(`${BASE_URL}/${id}/sites`);
  },

  /**
   * Actualizar vinculaciones de centros
   */
  updateSiteLinks: async (
    id: string,
    data: UpdateMedicalActSiteLinksRequest,
  ): Promise<{ success: boolean; message: string }> => {
    return api.patch<{ success: boolean; message: string }>(
      `${BASE_URL}/${id}/sites`,
      data,
    );
  },

  /**
   * Crear
   */
  create: async (data: CreateMedicalActRequest): Promise<MedicalAct> => {
    return api.post<MedicalAct>(BASE_URL, data);
  },

  /**
   * Actualizar
   */
  update: async (id: string, data: UpdateMedicalActRequest): Promise<MedicalAct> => {
    return api.patch<MedicalAct>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Desactivar
   */
  deactivate: async (id: string): Promise<MedicalAct> => {
    return api.delete<MedicalAct>(`${BASE_URL}/${id}`);
  },

  /**
   * Reactivar
   */
  reactivate: async (id: string): Promise<MedicalAct> => {
    return api.patch<MedicalAct>(`${BASE_URL}/${id}/reactivate`);
  },
};

export default medicalActsApi;
